import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment from 'moment'
import {
  Box,
  Grid,
  Typography,
  Avatar,
  LinearProgress,
  Paper,
  Alert,
  Tooltip,
  Stack,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { ExitToApp } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import AssignmentIcon from '@mui/icons-material/Assignment'
import GavelIcon from '@mui/icons-material/Gavel'
import SensorsIcon from '@mui/icons-material/Sensors'
import { CloudDone, CloudOff, Sync, WarningAmber } from '@mui/icons-material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DnsIcon from '@mui/icons-material/Dns'
import MapIcon from '@mui/icons-material/Map'
import LayersIcon from '@mui/icons-material/Layers'
import ImageIcon from '@mui/icons-material/Image'
import RouteIcon from '@mui/icons-material/Route'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { UserContext } from '../../../../App'
import Resources from './Resources'
import ChatDrawer from './ChatDrawer'
import { playChatPing, playStandDownAck } from './chatPing'
import { useControlRoomSnapshot } from './snapshot'
import { hasScaledEventMap, preferredMapView } from './eventMapGeo'
import TooltipToggleButton from './TooltipToggleButton'
import API from '../../../../services/API'
import CommandLogs from './CommandLogs'
import NewCommandLog from './NewCommandLog'
import ActiveIncidents from './ActiveIncidents'
import NewIncidents from './NewIncidents'
import NewIncident from './NewIncident'
import SelectedIncident from './SelectedIncident'
import InfoPopup from './InfoPopup'
import NewComplexDecision from './ComplexDecisions/NewComplexDecision'
import DecisionRegister from './ComplexDecisions/DecisionRegister'
import { useMatch, useNavigate } from 'react-router-dom'
import TreatmentCentreSummary from './TreatmentCentreSummary'
import TreatmentCentreManager from './TreatmentCentres/Configuration'
import BigModal from '../../../../components/Modals/BigModal'
import Metrics from './Metrics/Metrics'
import PoweredBySmall from '../../../../components/Corporate/PoweredBySmall'
import EventControlMap from './EventControlMap'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'
import ControlRoomHoursNotice from '../../../../components/Subscriptions/ControlRoomHoursNotice'

export const ControlRoomContext = createContext({})

const syncConfig = {
  live: {
    icon: <CloudDone fontSize="small" />,
    label: 'Live',
    color: '#16a34a',
  },
  delayed: {
    icon: <WarningAmber fontSize="small" />,
    label: 'Delayed',
    color: '#f59e0b',
  },
  offline: {
    icon: <CloudOff fontSize="small" />,
    label: 'Offline',
    color: '#dc2626',
  },
  connecting: {
    icon: <Sync fontSize="small" className="spin" />,
    label: 'Connecting',
    color: '#2563eb',
  },
}

const HeaderClock = () => {
  const [currentTime, setCurrentTime] = useState(moment())

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(moment()), 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <Stack alignItems={{ xs: 'flex-start', lg: 'center' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AccessTimeIcon sx={{ opacity: 0.7 }} />
        <Typography
          variant="h4"
          fontWeight={900}
          sx={{
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 1,
          }}
          color="white"
        >
          {moment(currentTime).format('HH:mm:ss')}
        </Typography>
      </Stack>

      <Typography variant="caption" sx={{ opacity: 0.68 }}>
        {moment(currentTime).format('dddd, Do MMM YYYY')}
      </Typography>
    </Stack>
  )
}

export const ActionBarItem = ({
  icon,
  text,
  onClick,
  danger = false,
  active = false,
  badge,
}) => (
  <Tooltip title={text}>
    <Button
      onClick={onClick}
      startIcon={icon}
      sx={{
        height: 42,
        minWidth: 'auto',
        px: 1.75,
        borderRadius: 2,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        color: danger ? '#FCA5A5' : '#E2E8F0',
        backgroundColor: active ? 'rgba(35,154,164,0.18)' : 'transparent',
        border: active
          ? '1px solid rgba(35,154,164,0.4)'
          : '1px solid rgba(255,255,255,0.08)',
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.875rem',
        gap: 0.5,

        '&:hover': {
          backgroundColor: danger
            ? 'rgba(239,68,68,0.15)'
            : 'rgba(255,255,255,0.06)',
          borderColor: danger
            ? 'rgba(239,68,68,0.4)'
            : 'rgba(255,255,255,0.15)',
        },
      }}
    >
      {text}

      {badge !== undefined && (
        <Box
          sx={{
            ml: 0.5,
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: active ? '#239AA4' : 'rgba(255,255,255,0.1)',
            color: 'white',
            fontSize: '0.75rem',
            fontWeight: 800,
          }}
        >
          {badge}
        </Box>
      )}
    </Button>
  </Tooltip>
)

const EventControlPage = ({ selectedControlRoom, onExit }) => {
  const { setSiteMode, siteMode } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  const {setPageTitle} = useDocumentTitle()
  const [linkedEvent, setLinkedEvent] = useState(null)
  const [eventMap, setEventMap] = useState(null)
  const [gpxRoutes, setGpxRoutes] = useState([])
  const [openNewIncident, setOpenNewIncident] = useState(false)
  const [selectedIncident, setSelectedIncident] = useState(null)
  const [showControlRoomInfo, setShowControlRoomInfo] = useState(false)
  const [openDecisionRegister, setOpenDecisionRegister] = useState(false)
  const [openNewComplexDecision, setOpenNewComplexDecision] = useState(false)
  const [openComplexDecision, setOpenComplexDecision] = useState(false)
  const [controlRoomView, setControlRoomView] = useState('compact')
  const [mapView, setMapView] = useState('map')
  const [openTreatmentCentreManager, setOpenTreatmentCentreManager] =
    useState(false)
  const [selectedTreatmentCentreID, setSelectedTreatmentCentreID] =
    useState(null)
  const [bedSummary, setBedSummary] = useState(null)
  const treatmentSummaryRef = useRef(null)
  const [opsLogsMaxHeight, setOpsLogsMaxHeight] = useState(620)

  const { data, loading, linkedEventID, refresh, syncState, lastUpdated } =
    useControlRoomSnapshot(selectedControlRoom, 1000)

  const resources = data?.resources ?? []
  const mapPins = data?.mapPins ?? []

  // The snapshot carries a coordinate-free GPX summary; when it stops
  // matching what we hold (another controller added/renamed/deleted a
  // route), refetch the full routes from /controlroominfo.
  const gpxSignature = (routes) =>
    (routes ?? [])
      .map((r) => `${r._id ?? r.id}:${r.name}:${r.color ?? ''}`)
      .join('|')
  const gpxRoutesRef = useRef(gpxRoutes)
  gpxRoutesRef.current = gpxRoutes
  useEffect(() => {
    if (!data?.gpxRoutes) return
    if (gpxSignature(data.gpxRoutes) === gpxSignature(gpxRoutesRef.current))
      return
    API.get(`/controlroominfo/${selectedControlRoom}`)
      .then((res) => {
        if (res.status === 200) setGpxRoutes(res.data.gpxRoutes ?? [])
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.gpxRoutes, selectedControlRoom])
  const onShiftUnassigned = data?.onShiftUnassigned ?? []
  const activeIncidents = data?.activeIncidents ?? []

  // Audible alert when a crew acknowledges a stand-down — the timeline log
  // alone is easy to miss. Compares snapshot polls; the first snapshot after
  // opening (or switching room) only seeds the baseline.
  const standDownAckSeenRef = useRef(null)
  useEffect(() => {
    standDownAckSeenRef.current = null
  }, [selectedControlRoom])
  useEffect(() => {
    if (!data) return
    const current = new Set()
    ;(data.activeIncidents ?? []).forEach((incident) => {
      ;(incident.assignedResources ?? []).forEach((row) => {
        if (row.standDownAcknowledgedAt) {
          current.add(`${incident.id}:${row._id}`)
        }
      })
    })
    const previous = standDownAckSeenRef.current
    standDownAckSeenRef.current = current
    if (!previous) return
    for (const key of current) {
      if (!previous.has(key)) {
        playStandDownAck()
        break
      }
    }
  }, [data])
  const { dispatchListIncidents, ongoingIncidents } = useMemo(
    () =>
      activeIncidents.reduce(
        (acc, incident) => {
          if (incident.status === 'New') {
            acc.dispatchListIncidents.push(incident)
          } else {
            acc.ongoingIncidents.push(incident)
          }

          return acc
        },
        { dispatchListIncidents: [], ongoingIncidents: [] },
      ),
    [activeIncidents],
  )

  const title = linkedEvent?.title ?? data?.customTitle ?? 'Control Room'
  const status = data?.status ?? 'Unknown'
  const metrics = useMemo(
    () => ({
      ...(data?.metrics ?? {}),
      epcrs: data?.epcrs ?? [],
    }),
    [data?.epcrs, data?.metrics],
  )

  const sync = syncConfig[syncState]
  const treatmentLocations =
    bedSummary?.locations || data?.treatmentLocations || []

  // Mirror of the server's cadLevels tiering (helpers/permissions.js) — the
  // server is authoritative on every endpoint; these flags only decide what
  // UI to draw. Viewer = read-only board; Call Taker/Dispatcher = operational
  // actions; Commander = room lifecycle + command decisions.
  const isSiteAdmin = !!user?.security?.isSiteAdmin
  const cadAccess = user?.employee?.cadAccess
  const cadLevel = isSiteAdmin
    ? 3
    : ({ Viewer: 1, 'Call Taker': 2, Dispatcher: 2, Commander: 3 }[cadAccess] ??
      0)
  // A closed room is read-only for everyone (the server refuses its writes
  // with 409 controlRoomClosed); a commander can still reopen it from
  // Control Room Info, so isCommander itself is unchanged.
  const isClosed = data?.status === 'Closed'
  const canDispatch = cadLevel >= 2 && !isClosed
  const isCommander = cadLevel >= 3
  const roleLabel = isSiteAdmin ? 'Site Admin' : cadAccess || 'Viewer'
  const canManageEventMap = isCommander && !isClosed

  useEffect(() => {
    const node = treatmentSummaryRef.current
    if (!node || typeof ResizeObserver === 'undefined') return undefined

    const updateLogsHeight = () => {
      setOpsLogsMaxHeight(Math.max(620, Math.ceil(node.offsetHeight)))
    }

    updateLogsHeight()
    const observer = new ResizeObserver(updateLogsHeight)
    observer.observe(node)

    return () => observer.disconnect()
  }, [controlRoomView, bedSummary, treatmentLocations])

  useEffect(() => {
    API.get(`/controlroominfo/${selectedControlRoom}`)
      .then((res) => {
        if (res.status === 200) {
          setLinkedEvent(res.data.linkedEvent)
          setEventMap(res.data.eventMap)
          setGpxRoutes(res.data.gpxRoutes ?? [])
          // Overlay (to-scale map) > site plan > plain map
          setMapView(preferredMapView(res.data.eventMap))
        }
      })
      .catch((e) => {
        console.log(e)
      })
  }, [selectedControlRoom])

  useEffect(() => {
    if (!selectedControlRoom) return

    API.get(
      `/commandcontrol/bed-management/summary?controlRoom=${selectedControlRoom}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setBedSummary(res.data)
        }
      })
      .catch((e) => {
        console.log(e)
      })
  }, [selectedControlRoom, data?.version])

  useEffect(() => {
    if (title) {
      setPageTitle(`${title} Control Room`)
    }
  }, [title, setPageTitle])

  // useEffect(() => {
  //   setSiteMode('eventControl')
  // }, [])

  // useEffect(() => {
  //   const handleBack = () => setSiteMode('normal')
  //   window.addEventListener('popstate', handleBack)

  //   return () => window.removeEventListener('popstate', handleBack)
  // }, [setSiteMode])

  const stale = syncState === 'offline' || syncState === 'delayed'

  // Resource ↔ control chat: drawer + unread summaries shared via context so
  // the metrics bar, resource cards, and resource drawer can all open it
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false)
  const [chatResourceID, setChatResourceID] = useState(null)
  const [chatSummaries, setChatSummaries] = useState([])
  const previousChatSummariesRef = useRef(null)
  const lastChatPingAtRef = useRef(0)
  const openChatRef = useRef({ open: false, resourceID: null })
  openChatRef.current = { open: chatDrawerOpen, resourceID: chatResourceID }

  // Crew messages stay unread until replied to or explicitly marked read, so
  // the ping also re-fires as a reminder while any remain outstanding.
  const CHAT_REMINDER_PING_MS = 30000

  const refreshChatSummaries = useCallback(() => {
    if (!selectedControlRoom) return
    API.get(`/commandcontrol/${selectedControlRoom}/chat-summaries`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          // Audible ping for new crew messages — unless someone at this
          // station already has that resource's conversation open
          const previous = previousChatSummariesRef.current
          if (previous) {
            const previousByResource = new Map(
              previous.map((summary) => [String(summary.resource), summary]),
            )
            const unseenUnread = res.data.filter((summary) => {
              if (!(summary.unreadFromCrew > 0)) return false
              const viewingThisChat =
                openChatRef.current.open &&
                String(openChatRef.current.resourceID) ===
                  String(summary.resource)
              return !viewingThisChat
            })
            const hasNewUnseenCrewMessage = unseenUnread.some((summary) => {
              const before = previousByResource.get(String(summary.resource))
              return summary.unreadFromCrew > (before?.unreadFromCrew ?? 0)
            })
            const reminderDue =
              unseenUnread.length > 0 &&
              Date.now() - lastChatPingAtRef.current >= CHAT_REMINDER_PING_MS
            if (hasNewUnseenCrewMessage || reminderDue) {
              playChatPing()
              lastChatPingAtRef.current = Date.now()
            }
          }
          previousChatSummariesRef.current = res.data
          setChatSummaries(res.data)
        }
      })
      .catch(() => {})
  }, [selectedControlRoom])

  useEffect(() => {
    refreshChatSummaries()
    const interval = setInterval(refreshChatSummaries, 6000)
    return () => clearInterval(interval)
  }, [refreshChatSummaries])

  const chatUnreadTotal = useMemo(
    () =>
      chatSummaries.reduce(
        (total, summary) => total + (summary.unreadFromCrew ?? 0),
        0,
      ),
    [chatSummaries],
  )

  const openResourceChat = useCallback((resource) => {
    setChatResourceID(
      resource ? String(resource._id ?? resource.id ?? resource) : null,
    )
    setChatDrawerOpen(true)
  }, [])

  const contextValue = useMemo(
    () => ({
      openNewIncident,
      setOpenNewIncident,
      selectedIncident,
      setSelectedIncident,
      selectedControlRoom,
      linkedEvent,
      eventMap,
      mapView,
      activeIncidents,
      metrics,
      resources,
      onShiftUnassigned,
      refresh,
      chatSummaries,
      chatUnreadTotal,
      openResourceChat,
      refreshChatSummaries,
      canDispatch,
      isCommander,
    }),
    [
      activeIncidents,
      linkedEvent,
      eventMap,
      mapView,
      metrics,
      openNewIncident,
      refresh,
      selectedControlRoom,
      selectedIncident,
      resources,
      onShiftUnassigned,
      chatSummaries,
      chatUnreadTotal,
      openResourceChat,
      refreshChatSummaries,
      canDispatch,
      isCommander,
    ],
  )

  return (
    <ControlRoomContext.Provider value={contextValue}>
      <Box
        minHeight="100vh"
        sx={{
          background:
            'radial-gradient(circle at top left, rgba(35,154,164,0.18), transparent 30%), linear-gradient(135deg, #0F172A 0%, #1A2230 45%, #101827 100%)',
        }}
      >
        {loading && (
          <LinearProgress
            sx={{
              height: 3,
              backgroundColor: 'rgba(255,255,255,0.12)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#239AA4',
              },
            }}
          />
        )}

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1,
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            background: stale
              ? 'linear-gradient(135deg, #4A1D1D 0%, #2A1212 100%)'
              : 'linear-gradient(135deg, rgba(36,47,64,0.92), rgba(26,34,48,0.96))',
            boxShadow: '0 10px 32px rgba(0,0,0,0.24)',
          }}
        >
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} lg={4}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, #242F40 0%, #239AA4 130%)',
                    boxShadow: '0 12px 30px rgba(35,154,164,0.25)',
                    flexShrink: 0,
                  }}
                >
                  <SensorsIcon />
                </Box>

                <Stack spacing={0.25} minWidth={0}>
                  <Typography variant="overline" sx={{ opacity: 0.72 }}>
                    Command & Control
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={900}
                    noWrap
                    sx={{
                      textShadow: '0 2px 12px rgba(0,0,0,0.28)',
                      // Phones have no room for a one-line title — wrap it.
                      whiteSpace: { xs: 'normal', lg: 'nowrap' },
                    }}
                    color="white"
                  >
                    {title}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      icon={
                        <FiberManualRecordIcon
                          sx={{
                            fontSize: '0.8rem !important',
                            color: status === 'Active' ? '#22C55E' : '#94A3B8',
                          }}
                        />
                      }
                      label={status}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.10)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.16)',
                        fontWeight: 800,
                      }}
                    />

                    {data?.controlRoomRef && (
                      <Chip
                        label={`Room ${data.controlRoomRef}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(35,154,164,0.18)',
                          color: '#A7F3F8',
                          border: '1px solid rgba(35,154,164,0.32)',
                          fontWeight: 800,
                        }}
                      />
                    )}

                    {!canDispatch && (
                      <Chip
                        icon={
                          <VisibilityIcon
                            sx={{
                              fontSize: '0.9rem !important',
                              color: '#FCD34D !important',
                            }}
                          />
                        }
                        label="View Only"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(245,158,11,0.18)',
                          color: '#FCD34D',
                          border: '1px solid rgba(245,158,11,0.38)',
                          fontWeight: 800,
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack height="100%" justifyContent="space-between">
                <HeaderClock />
                <PoweredBySmall />
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack gap={2}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                  spacing={1.5}
                >
                  {/* Sync Status */}
                  <Stack alignItems="center" spacing={0.5}>
                    <Chip
                      icon={sync?.icon}
                      label={sync?.label || 'Unknown'}
                      size="small"
                      sx={{
                        backgroundColor: sync?.color,
                        color: 'white',
                        fontWeight: 800,
                        minWidth: 110,
                        '& .MuiChip-icon': {
                          color: 'white',
                        },
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        opacity: syncState === 'live' ? 0.7 : 1,
                        color:
                          syncState === 'offline'
                            ? '#fca5a5'
                            : syncState === 'delayed'
                              ? '#fcd34d'
                              : 'inherit',
                        fontVariantNumeric: 'tabular-nums',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {syncState === 'offline'
                        ? 'Connection lost'
                        : syncState === 'delayed'
                          ? `Delayed • Last update ${moment(lastUpdated).format('HH:mm:ss')}`
                          : lastUpdated
                            ? `Updated ${moment(lastUpdated).fromNow()}`
                            : 'Waiting for sync'}
                    </Typography>
                  </Stack>
                  <Stack
                    textAlign={{ xs: 'left', lg: 'right' }}
                    alignItems="flex-start"
                    justifyContent="flex-start"
                  >
                    <Typography variant="body2" fontWeight={800} color="white">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.68 }}
                      color="white"
                    >
                      {canDispatch
                        ? `${roleLabel} • Logged in operator`
                        : `${roleLabel} • View only`}
                    </Typography>
                  </Stack>

                  <Avatar
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 3,
                      border: '2px solid rgba(255,255,255,0.24)',
                      backgroundColor: '#239AA4',
                    }}
                    src={user?.photograph}
                  >
                    {user.firstName?.[0]}
                    {user.lastName?.[0]}
                  </Avatar>
                </Stack>
                <Metrics />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            // Phones: let the action bar wrap onto extra rows rather than
            // pushing the view toggles / New Incident off the right edge.
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            gap: 1,
            backgroundColor: '#111827',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <ActionBarItem icon={<ExitToApp />} text="Exit" onClick={onExit} />

          <ActionBarItem
            icon={<AssignmentIcon />}
            text="Control Room Info"
            onClick={() => setShowControlRoomInfo(true)}
          />
          <ActionBarItem
            icon={<GavelIcon />}
            color="#1E7F88"
            text="Complex Decisions"
            onClick={() => setOpenDecisionRegister(true)}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={controlRoomView}
            onChange={(_, nextView) => nextView && setControlRoomView(nextView)}
            sx={{
              ml: { xs: 0, lg: 1 },
              flexShrink: 0,
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              overflow: 'hidden',
              '& .MuiToggleButton-root': {
                color: '#CBD5E1',
                border: 0,
                px: 1.5,
                py: 0.8,
                gap: 0.75,
                textTransform: 'none',
                fontWeight: 900,
                '&.Mui-selected': {
                  color: 'white',
                  backgroundColor: 'rgba(35,154,164,0.32)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(35,154,164,0.42)',
                },
              },
            }}
          >
            <ToggleButton value="compact">
              <DashboardIcon fontSize="small" />
              Compact
            </ToggleButton>
            <ToggleButton value="ops">
              <DnsIcon fontSize="small" />
              Screen 1
            </ToggleButton>
            <ToggleButton value="map">
              <MapIcon fontSize="small" />
              Screen 2
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mapView}
            onChange={(_, nextView) => nextView && setMapView(nextView)}
            sx={{
              flexShrink: 0,
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              overflow: 'hidden',
              '& .MuiToggleButton-root': {
                color: '#CBD5E1',
                border: 0,
                px: 1.5,
                py: 0.8,
                gap: 0.75,
                textTransform: 'none',
                fontWeight: 900,
                '&.Mui-selected': {
                  color: 'white',
                  backgroundColor: 'rgba(35,154,164,0.32)',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(35,154,164,0.42)',
                },
              },
            }}
          >
            <ToggleButton value="map">
              <MapIcon fontSize="small" />
              Map
            </ToggleButton>
            <ToggleButton value="event">
              <ImageIcon fontSize="small" />
              Event
            </ToggleButton>
            <TooltipToggleButton
              value="overlay"
              disabled={!hasScaledEventMap(eventMap)}
              title={
                hasScaledEventMap(eventMap)
                  ? ''
                  : 'Overlay needs an event map marked as drawn to scale (Manage on the event map)'
              }
            >
              <LayersIcon fontSize="small" />
              Overlay
            </TooltipToggleButton>
            <ToggleButton value="gpx">
              <RouteIcon fontSize="small" />
              GPX
            </ToggleButton>
          </ToggleButtonGroup>
          <Box flex={1} />
          <DecisionRegister
            open={openDecisionRegister}
            onClose={() => setOpenDecisionRegister(false)}
            controlRoomID={selectedControlRoom}
            openComplexDecision={openComplexDecision}
            setOpenComplexDecision={setOpenComplexDecision}
            setOpenNewComplexDecision={setOpenNewComplexDecision}
            canCreate={isCommander && !isClosed}
          />

          {canDispatch && (
            <NewCommandLog
              roomID={selectedControlRoom}
              linkedEventID={linkedEventID}
            />
          )}

          {canDispatch && (
            <ActionBarItem
              icon={<AddIcon />}
              text="New Incident"
              onClick={() => setOpenNewIncident(true)}
              flex={1}
              danger
            />
          )}
        </Box>

        {data?.status === 'Closed' && (
          <Box px={{ xs: 2, md: 3 }} mt={2}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
                border: '1px solid rgba(35,154,164,0.24)',
              }}
            >
              This control room is closed and read-only.
              {isCommander
                ? ' Open Control Room Info to reopen it.'
                : ' A commander can reopen it from Control Room Info.'}
            </Alert>
          </Box>
        )}

        {!isClosed && (
          <Box px={{ xs: 2, md: 3 }} mt={2}>
            <ControlRoomHoursNotice inRoom />
          </Box>
        )}

        {controlRoomView === 'compact' && (
          <Grid container spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
            <Resources
              controlRoomID={selectedControlRoom}
              resources={resources}
              gridProps={{
                xs: 12,
                lg: 5,
                order: { xs: 1, lg: 0 },
                sx: { display: 'flex' },
              }}
              compact
            />

            {/* Map pinned to the top of the right column with the treatment
                centres tucked beneath it. Previously the map sat alone in
                this column: a long resource list stretched the row and the
                map image floated halfway down it, with the treatment centres
                pushed below the whole list. */}
            <Grid
              item
              xs={12}
              lg={7}
              sx={{ order: { xs: 0, lg: 1 }, minWidth: 0 }}
            >
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    height: { xs: 420, lg: 480 },
                    flexShrink: 0,
                  }}
                >
                  <EventControlMap
                    resources={resources}
                    incidents={activeIncidents}
                    mapPins={mapPins}
                    mapView={mapView}
                    eventMap={eventMap}
                    controlRoomID={selectedControlRoom}
                    canManageEventMap={canManageEventMap}
                    onEventMapSaved={setEventMap}
                    gpxRoutes={gpxRoutes}
                    onGpxRoutesSaved={setGpxRoutes}
                    treatmentCentres={treatmentLocations}
                    onOpenTreatmentCentre={(locationID) => {
                      setSelectedTreatmentCentreID(locationID)
                      setOpenTreatmentCentreManager(true)
                    }}
                    compact
                    height="100%"
                  />
                </Box>
                <TreatmentCentreSummary
                  stats={bedSummary || {}}
                  locations={treatmentLocations}
                  onConfigureLocation={(locationID = null) => {
                    setSelectedTreatmentCentreID(locationID)
                    setOpenTreatmentCentreManager(true)
                  }}
                  gridProps={{ sx: { flex: 1, minHeight: 0, display: 'flex' } }}
                />
              </Stack>
            </Grid>

            <NewIncidents
              incidents={dispatchListIncidents}
              controlRoomRef={data?.controlRoomRef}
              controlRoomID={selectedControlRoom}
              gridProps={{
                xs: 12,
                lg: 5,
                order: { xs: -2, lg: -2 },
                sx: { display: 'flex' },
              }}
              compact
            />

            <ActiveIncidents
              incidents={ongoingIncidents}
              controlRoomRef={data?.controlRoomRef}
              controlRoomID={selectedControlRoom}
              gridProps={{
                xs: 12,
                lg: 7,
                order: { xs: -1, lg: -1 },
                sx: { display: 'flex' },
              }}
              compact
            />

            <Grid item xs={12} sx={{ order: 2 }}>
              <CommandLogs controlRoomID={selectedControlRoom} compact />
            </Grid>
          </Grid>
        )}

        {controlRoomView === 'ops' && (
          <Grid container spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
            <NewIncidents
              incidents={dispatchListIncidents}
              controlRoomRef={data?.controlRoomRef}
              controlRoomID={selectedControlRoom}
              gridProps={{
                xs: 12,
                lg: 5,
                sx: { display: 'flex' },
              }}
            />

            <ActiveIncidents
              incidents={ongoingIncidents}
              controlRoomRef={data?.controlRoomRef}
              controlRoomID={selectedControlRoom}
              gridProps={{
                xs: 12,
                lg: 7,
                sx: { display: 'flex' },
              }}
            />

            <TreatmentCentreSummary
              stats={bedSummary || {}}
              locations={treatmentLocations}
              onConfigureLocation={(locationID = null) => {
                setSelectedTreatmentCentreID(locationID)
                setOpenTreatmentCentreManager(true)
              }}
              gridProps={{
                xs: 12,
                lg: 7,
                ref: treatmentSummaryRef,
                sx: { display: 'flex' },
              }}
            />

            <Grid item xs={12} lg={5} sx={{ display: 'flex' }}>
              <CommandLogs
                controlRoomID={selectedControlRoom}
                maxHeight={{ xs: 520, lg: opsLogsMaxHeight }}
              />
            </Grid>
          </Grid>
        )}

        {controlRoomView === 'map' && (
          <Grid container spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
            <Grid item xs={12}>
              <EventControlMap
                resources={resources}
                incidents={activeIncidents}
                mapPins={mapPins}
                mapView={mapView}
                eventMap={eventMap}
                controlRoomID={selectedControlRoom}
                canManageEventMap={canManageEventMap}
                onEventMapSaved={setEventMap}
                gpxRoutes={gpxRoutes}
                onGpxRoutesSaved={setGpxRoutes}
                treatmentCentres={treatmentLocations}
                onOpenTreatmentCentre={(locationID) => {
                  setSelectedTreatmentCentreID(locationID)
                  setOpenTreatmentCentreManager(true)
                }}
                height="calc(100vh - 258px)"
              />
            </Grid>
          </Grid>
        )}

        <BigModal
          open={openTreatmentCentreManager}
          onClose={() => {
            setOpenTreatmentCentreManager(false)
            setSelectedTreatmentCentreID(null)
          }}
          hideBackgroundColor
        >
          <TreatmentCentreManager
            onClose={setOpenTreatmentCentreManager}
            controlRoomID={selectedControlRoom}
            initialCentreID={selectedTreatmentCentreID}
          />
        </BigModal>
      </Box>

      <NewIncident />
      <SelectedIncident data={data} />
      <NewComplexDecision
        open={openNewComplexDecision}
        onClose={() => setOpenNewComplexDecision(false)}
        controlRoomID={selectedControlRoom}
        linkedEventID={linkedEventID}
      />
      <InfoPopup
        open={showControlRoomInfo}
        onClose={() => setShowControlRoomInfo(false)}
        controlRoomInfo={selectedControlRoom}
        linkedEvent={linkedEvent}
        data={data}
        refreshControlRoom={refresh}
        isCommander={isCommander}
      />
      <ChatDrawer
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        resources={resources}
        summaries={chatSummaries}
        selectedResourceID={chatResourceID}
        onSelectResource={setChatResourceID}
        refreshSummaries={refreshChatSummaries}
      />
    </ControlRoomContext.Provider>
  )
}

const EventControlRoute = () => {
  const match = useMatch('/commandcontrol/controlroom/:controlRoomID')
  const controlRoomID = match?.params?.controlRoomID

  const navigate = useNavigate()
  const { setSiteMode } = useContext(SiteModeContext)

  useEffect(() => {
    if (controlRoomID) {
      setSiteMode('eventControl')
    } else {
      setSiteMode('normal')
    }
    return () => setSiteMode('normal')
  }, [controlRoomID, setSiteMode])

  const handleExit = () => {
    setSiteMode('normal')
    navigate('/commandcontrol/controlroom')
  }

  if (!controlRoomID) return null

  return (
    <EventControlPage selectedControlRoom={controlRoomID} onExit={handleExit} />
  )
}

export default EventControlRoute
